import { $t } from '@/i18n';
import { computed, onUnmounted, ref, shallowRef, watch, watchEffect } from '@vue/composition-api';
import { isEqual, memoize } from 'lodash';
import { DBSource, HTTPSource } from '@tencent/wuji-data-web';
import { createWujiData } from '@/modules/ai-page/wujibase/wuji-data';
import { AIPageContext } from '@/utils/global/api';
import { loadVue3Stuff } from './runtime-loader';
import { WUJI_DEPS_KEY } from '@utils/dependencyLoader/consts';
import { interceptWujiIfNeeded } from './check/setup';
import { platformBuiltinModules } from '@/utils/dependencyLoader/platform-builtin-modules';
import Wuji from '@/modules/ai-page/wujibase/wuji';
import WujiFlow from '@/modules/ai-page/wujibase/wuji-flow';
import * as wujiFlowVue from '@/modules/ai-page/wujibase/wuji-flow-vue';
import WujiUpload from '@/modules/ai-page/wujibase/wuji-upload';
import useAiDepLoader from './xy-logic/use-ai-dep-loader';
import { unifyAssetFileContents } from './unifyAssetFileContents';
const { loadAIPageDependency, } = useAiDepLoader();
const loadVueDependency = memoize(() => loadAIPageDependency(true, 'vue3'));
const loadReactDependency = memoize(() => loadAIPageDependency(true, 'react'));
export function getWujiBaseModules(opts = {}) {
    const wujiData = opts.wujiData || (opts.projectId ? createWujiData(opts.projectId) : undefined);
    return {
        '@wujibase/wuji': interceptWujiIfNeeded(new Wuji(), 'wuji'),
        '@wujibase/wuji-flow': interceptWujiIfNeeded(new WujiFlow(), 'wuji-flow'),
        '@wujibase/wuji-flow/vue': interceptWujiIfNeeded(wujiFlowVue, 'wuji-flow-vue'),
        '@wujibase/wuji-upload': interceptWujiIfNeeded(new WujiUpload(), 'wuji-upload'),
        ...(wujiData ? { '@wujibase/wuji-data': interceptWujiIfNeeded(wujiData, 'wuji-data') } : {}),
    };
}
export async function prepareGogoRuntimeEnv(opts) {
    // Load Vue3 dependencies
    const vue3stuff = await loadVue3Stuff();
    // Load external dependencies
    await loadVueDependency();
    // Prepare data from collections
    const wujiData = createWujiData(opts.projectId);
    const wujiBaseModules = getWujiBaseModules({ wujiData });
    // Create wuji context with data
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return wujiData;
        },
    });
    const globals = {
        wuji: interceptWujiIfNeeded(wuji, 'wuji'),
        __gogoAssetUrl: resolveAssetPath,
    };
    function customRequire(moduleName) {
        if (moduleName in vue3stuff) {
            return vue3stuff[moduleName];
        }
        if (moduleName in platformBuiltinModules) {
            return platformBuiltinModules[moduleName]();
        }
        if (moduleName in wujiBaseModules)
            return wujiBaseModules[moduleName];
        if (window[WUJI_DEPS_KEY]?.[moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        // 不知道抽啥风，资源文件也会过来
        const maybeAssetUrl = moduleName.startsWith('./') && resolveAssetPath(moduleName);
        if (maybeAssetUrl)
            return { default: maybeAssetUrl, __esModule: true };
        throw new Error($t('k_ff04ee3d', { arg_0: moduleName }));
    }
    function resolveAssetPath(relPath) {
        const { pathname } = new URL(relPath, `${location.origin}/dist/mock-entry`);
        return assetFiles?.[pathname]?.url;
    }
    return {
        globals,
        customRequire,
    };
}
const assetMimeTypes = {
    avif: 'image/avif',
    bmp: 'image/bmp',
    gif: 'image/gif',
    ico: 'image/x-icon',
    jpeg: 'image/jpeg',
    jpg: 'image/jpeg',
    png: 'image/png',
    svg: 'image/svg+xml',
    webp: 'image/webp',
    css: 'text/css',
    html: 'text/html',
    js: 'text/javascript',
    json: 'application/json',
    mjs: 'text/javascript',
    txt: 'text/plain',
    wasm: 'application/wasm',
    eot: 'application/vnd.ms-fontobject',
    otf: 'font/otf',
    ttf: 'font/ttf',
    woff: 'font/woff',
    woff2: 'font/woff2',
    mp4: 'video/mp4',
};
function getAssetPathname(path) {
    return path.split('?')[0].split('#')[0];
}
function getAssetFileName(path) {
    return getAssetPathname(path)
        .split('/')
        .pop() || 'asset.bin';
}
function getAssetMimeType(path) {
    const pathname = getAssetPathname(path);
    const dotIndex = pathname.lastIndexOf('.');
    if (dotIndex === -1)
        return '';
    return assetMimeTypes[pathname.slice(dotIndex + 1).toLowerCase()] || '';
}
let assetFiles = {}; // 格式类似 { '/dist/xxx': { url? }}
let applyAssetFilesVersion = 0;
function revokeBlobUrls(files) {
    for (const { url } of Object.values(files)) {
        if (url?.startsWith('blob:')) {
            URL.revokeObjectURL(url);
        }
    }
}
export async function applyAssetFiles(newAssetFiles) {
    const version = applyAssetFilesVersion + 1;
    applyAssetFilesVersion = version;
    // newAssetFiles: { '/dist/xxx': { url?, content? } }
    const result = {};
    for (const [path, file] of Object.entries(newAssetFiles)) {
        if (!file.url && (file.contentGzipBase64 || file.contents)) {
            const bytes = await unifyAssetFileContents(file) || new Uint8Array(0);
            const type = file.type || file.contentType || file.mimeType || getAssetMimeType(path);
            const blob = new File([bytes], getAssetFileName(path), { type });
            result[path] = { url: URL.createObjectURL(blob) };
        }
        else {
            result[path] = { url: file.url };
        }
    }
    if (version !== applyAssetFilesVersion) {
        revokeBlobUrls(result);
        return;
    }
    revokeBlobUrls(assetFiles);
    assetFiles = result;
}
export async function prepareReactGogoRuntimeEnv(opts) {
    const w = window;
    const reactPackages = {};
    await Promise.all([
        // @ts-ignore
        import('@tencent/wuji-react/react').then((e) => {
            reactPackages.react = e;
            w.React = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/react-dom').then((e) => {
            reactPackages['react-dom'] = e;
            w.ReactDOM = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/react-dom/client').then((e) => {
            reactPackages['react-dom/client'] = e;
            w.ReactDOMClient = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/react-router-dom').then((e) => {
            reactPackages['react-router-dom'] = e;
            w.ReactRouterDOM = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/react/jsx-runtime').then((e) => {
            reactPackages['react/jsx-runtime'] = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/react/jsx-dev-runtime').then((e) => {
            reactPackages['react/jsx-dev-runtime'] = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/zustand').then((e) => {
            reactPackages.zustand = e;
            w.Zustand = e;
        }),
        // @ts-ignore
        import('@tencent/wuji-react/zustand/shallow').then((e) => {
            reactPackages['zustand/shallow'] = e;
        }),
    ]);
    await loadReactDependency();
    const wujiData = createWujiData(opts.projectId);
    const wujiBaseModules = getWujiBaseModules({ wujiData });
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return wujiData;
        },
    });
    const globals = { wuji: interceptWujiIfNeeded(wuji, 'wuji') };
    function customRequire(moduleName) {
        if (moduleName in reactPackages) {
            return reactPackages[moduleName];
        }
        if (moduleName in platformBuiltinModules) {
            return platformBuiltinModules[moduleName]();
        }
        if (moduleName in wujiBaseModules)
            return wujiBaseModules[moduleName];
        if (window[WUJI_DEPS_KEY]?.[moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        throw new Error($t('k_ff04ee3d', { arg_0: moduleName }));
    }
    return {
        globals,
        customRequire,
    };
}
export function useGogoRuntimeEnv(opts) {
    const { data, isLoadingData } = useData(opts);
    const wuji = new AIPageContext();
    Object.defineProperty(wuji, 'data', {
        get() {
            return data.value;
        },
    });
    const latestLoadExternalsPromiseAbortController = shallowRef(null);
    const latestLoadExternalsResult = shallowRef(null);
    onUnmounted(() => latestLoadExternalsPromiseAbortController.value?.abort());
    const vue3stuff = shallowRef();
    const isVue3StuffReady = computed(() => !!vue3stuff.value);
    loadVue3Stuff().then((c) => {
        vue3stuff.value = c;
    });
    const getGlobals = () => ({ wuji: interceptWujiIfNeeded(wuji, 'wuji') });
    const wujiBaseModules = getWujiBaseModules();
    function customRequire(moduleName) {
        if (moduleName in vue3stuff.value) {
            // 加载 ant-design-vue 和 tdesign-vue-next
            return vue3stuff.value[moduleName];
        }
        if (moduleName in platformBuiltinModules) {
            return platformBuiltinModules[moduleName]();
        }
        if (moduleName in wujiBaseModules)
            return wujiBaseModules[moduleName];
        if (window[WUJI_DEPS_KEY]?.[moduleName]) {
            return window[WUJI_DEPS_KEY][moduleName];
        }
        throw new Error($t('k_ff04ee3d', { arg_0: moduleName }));
    }
    watchEffect(() => {
        console.log('[wuji-gogo] latestLoadExternalsResult changed', latestLoadExternalsResult.value);
    });
    return {
        getGlobals,
        customRequire,
        isExternalsLoading: computed(() => (isLoadingData.value || !latestLoadExternalsResult.value || !isVue3StuffReady.value)),
        externalsLoadError: computed(() => latestLoadExternalsResult.value?.error),
    };
}
function useData(opts) {
    const { projectId, } = opts.value;
    const data = ref({});
    const isLoadingData = ref(false);
    const getData = async () => {
        const data = {};
        await Promise.all(opts.value.collectionRefs.map(async (collection) => {
            const { collectionId, type } = collection;
            let dataSource;
            if (type === 'db') {
                dataSource = new DBSource({
                    collectionId,
                    projectId,
                });
            }
            else {
                dataSource = new HTTPSource({
                    collectionId,
                    projectId,
                });
            }
            data[collectionId] = await dataSource.getMethods();
        }));
        return data;
    };
    watch(() => opts.value.collectionRefs, async (newValue, oldValue) => {
        if (isEqual(newValue, oldValue))
            return;
        isLoadingData.value = true;
        const dataSource = await getData();
        // 考虑await时间差问题，这里保护一下
        if (opts.value.collectionRefs !== newValue)
            return;
        data.value = dataSource;
        isLoadingData.value = false;
    }, { immediate: true });
    return {
        data,
        isLoadingData,
    };
}
